import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import SquareIcon from '@mui/icons-material/Square'
import UploadIcon from '@mui/icons-material/Upload'
import { IconButton, styled } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid2'
import MenuItem from '@mui/material/MenuItem'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useFormik } from 'formik'
import { map } from 'lodash'
import * as React from 'react'
import { useState } from 'react'
import { FileUploader } from 'react-drag-drop-files'
import * as yup from 'yup'
import CloseIcon from '../../../assets/icons/Close.svg'
import { Notistack } from '../../../components/Notistack'
import { BlockWrapper } from '../../../components/Wrapper/BlockWrapper'
import { usePackageSubscription } from '../../../hooks/usePackageSubscription'
import { usePersonalDiscount } from '../../../hooks/usePersonalDiscount'
import { useAssistantAdministrationStore } from '../../../store/storeAssistantAdministration'
import { useProfileStore } from '../../../store/storeProfile'
import { useWrapperStore } from '../../../store/storeWrapper'
import { fileSizeValidator, fileTypeValidator } from '../../../validators/fileTypeValidator'

const HeaderWrapper = styled(Grid)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    borderBottom: '1px solid #F4F4F6',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
}))

const InnerWrapper = styled(Grid)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
    },
    columnGap: '24px',
}))

const ActionsWrapper = styled(Grid)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: '16px',
    [theme.breakpoints.down('sm')]: {
        justifyContent: 'center',
    },
}))

const ProjectSelectWrapper = styled(Grid)(({ theme }) => ({
    display: 'flex',
    borderBottom: '1px solid #F4F4F6',
    mb: 2,
    padding: '0 32px',
    [theme.breakpoints.down('sm')]: {
        padding: '0 16px',
    },
}))

const fileTypes = [
    'mp4',
    'mov',
    'wmv',
    'avi',
    'mkv',
    'webm',
    'mp3',
    'm4a',
    'aac',
    'wav',
    'flac',
    'ogg',
]

const validationSchema1 = yup.object({
    title: yup.string().required('Пожалуйста, введите название'),
    url: yup
        .string()
        .matches(/^(https?:\/\/)?[\w.-]+(?:\.[a-z]{2,})+([\/?#][^\s]*)?$/, 'Ссылка некорректна')
        .required('Пожалуйста, введите ссылку на запись'),
    idMemoPrompt: yup.string().required('Пожалуйста, выберите типо саммари '),
})

const validationSchema2 = yup.object({
    title: yup.string().required('Пожалуйста, введите название'),
    idMemoPrompt: yup.string().required('Пожалуйста, выберите типо саммари '),
})

export const RecordUploadPage = () => {
    const {
        createRecord,
        uploadFile,
        fetchMemoPrompt,
        memoPrompt,
        errorCreateRecord,
        errorUploadFile,
        isLoadingCreateRecord,
        isLoadingUploadFile,
        isLoadingUpdateRecord,
        clearCreateRecordError,
        clearUploadFileError,
    } = useAssistantAdministrationStore((state) => state)
    const { projectListActive } = useWrapperStore((state) => state)
    const { isFullAccess } = useProfileStore((state) => state)

    const [files, setFile] = useState<File>(null)
    const [mode, setMode] = React.useState(0)
    const [fileChangeError, setFileChangeError] = useState('')
    const [showSucces, setShowSuccess] = useState(false)

    const { promotePersonalDiscount } = usePersonalDiscount()
    const { promoteUpdatePackageSubscription } = usePackageSubscription()

    React.useEffect(() => {
        fetchMemoPrompt()
    }, [])

    React.useEffect(() => {
        const [defaultProject] = projectListActive || []

        formik.setFieldValue('workplaceId', defaultProject?.id)
    }, [projectListActive])

    const handleChangeMode = (event: React.SyntheticEvent, newValue: number) => {
        setMode(newValue)
    }

    const handleChange = async (file) => {
        if (!fileTypeValidator(file)) {
            setFileChangeError('Неподдерживаемый формат файла')
        } else if (!fileSizeValidator(file)) {
            setFileChangeError('Файл слишком большой, выберите файл меньшего размера')
        } else {
            setFile(file)
        }
    }

    const formik = useFormik({
        initialValues: {
            title: '',
            extra_context: '',
            file: '',
            idMemoPrompt: '',
            workplaceId: '',
            url: '',
        },
        validationSchema: mode === 0 ? validationSchema1 : validationSchema2,
        enableReinitialize: true,
        onSubmit: (values, { resetForm, setFieldValue }) => {
            const [defaultProject] = projectListActive || []
            const { idMemoPrompt, title, extra_context, workplaceId, url } = values
            if (mode === 0) {
                createRecord({
                    title,
                    extra_context,
                    memo_prompt_id: idMemoPrompt,
                    source: url,
                    workplace_id: workplaceId,
                    source_type: 'EXTERNAL',
                })
            } else {
                uploadFile({
                    extra_context,
                    title,
                    file: files,
                    workplaceId: workplaceId,
                    memo_prompt_id: idMemoPrompt,
                })
            }

            setShowSuccess(true)

            setFile(null)

            resetForm()

            setFieldValue('workplaceId', defaultProject?.id)

            promotePersonalDiscount()
            promoteUpdatePackageSubscription()
        },
    })

    const handleCloseSuccess = (event: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return
        }

        setShowSuccess(false)
    }

    const handleCloseSnackbar = (event: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return
        }

        clearCreateRecordError()
        clearUploadFileError()
        setFileChangeError('')
    }

    const handleDeleteFile = () => {
        setFile(null)
    }

    return (
        <BlockWrapper>
            <HeaderWrapper>
                <Typography variant="h4">Расшифровать запись</Typography>
            </HeaderWrapper>
            <ProjectSelectWrapper>
                <FormControl
                    fullWidth
                    sx={{
                        my: 0,
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'row',
                    }}>
                    <Typography variant="h6" sx={{ mr: 2 }}>
                        Куда:
                    </Typography>
                    <TextField
                        color="info"
                        slotProps={{
                            input: {
                                disableUnderline: true,
                            },
                        }}
                        sx={{
                            border: 'none',
                            '& fieldset': { border: 'none' },
                        }}
                        select
                        id="workplaceId"
                        name="workplaceId"
                        value={formik.values.workplaceId}
                        onChange={formik.handleChange}
                        error={formik.touched.workplaceId && Boolean(formik.errors.workplaceId)}
                        onBlur={formik.handleBlur}>
                        {map(projectListActive, (item, index) => {
                            return (
                                <MenuItem value={item.id}>
                                    <SquareIcon
                                        sx={{
                                            fontSize: '14px',
                                            color: '#006CFD',
                                            marginRight: '8px',
                                        }}
                                    />
                                    {item.title}
                                </MenuItem>
                            )
                        })}
                    </TextField>
                </FormControl>
            </ProjectSelectWrapper>

            <Grid sx={{ width: '100%', padding: '16px' }}>
                <form onSubmit={formik.handleSubmit}>
                    {memoPrompt && memoPrompt.length === 0 && (
                        <Typography color="error" variant="h4" sx={{ margin: '0 auto ' }}>
                            {`Расшифровать запись не может быть выполнена из-за того, в выбранных моделях отсутствуют виды расшифровок. Сделать их доступными можно в глобальных настройках`}
                        </Typography>
                    )}
                    {memoPrompt && memoPrompt.length > 0 && (
                        <>
                            <InnerWrapper>
                                <Grid>
                                    <Tabs
                                        value={mode}
                                        onChange={handleChangeMode}
                                        sx={{ pt: 0, borderBottom: 'none' }}>
                                        <Tab label="Вставить ссылку" id={0} />
                                        <Tab label="Загрузить с устройства" id={1} />
                                    </Tabs>

                                    {mode === 0 && (
                                        <>
                                            <Typography color="text" variant="h5" sx={{ mt: 2 }}>
                                                Ссылка на запись *
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ marginY: 1, color: '#9195A1' }}>
                                                Мы принимаем ссылки только из Google Drive, Яндекс
                                                Диск, Dropbox, VK Видео, Rutube, Одноклассники.
                                                Проверьте, что доступ к файлу открыт.
                                            </Typography>
                                            <TextField
                                                required
                                                sx={{
                                                    display: 'flex',
                                                    textAlign: 'left',
                                                    marginTop: '8px',
                                                }}
                                                margin="dense"
                                                id="url"
                                                name="url"
                                                placeholder="https://"
                                                fullWidth
                                                value={formik.values.url}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={
                                                    formik.touched.url && Boolean(formik.errors.url)
                                                }
                                                helperText={formik.touched.url && formik.errors.url}
                                            />
                                        </>
                                    )}
                                    {mode === 1 && (
                                        <>
                                            <Typography variant="h5" color="text" sx={{ mt: 2 }}>
                                                Я поддерживаю эти форматы:
                                            </Typography>
                                            <Typography variant="body2" sx={{ marginY: 1 }}>
                                                - видео — MP4, MOV, WMV, AVI, MKV, WEBM
                                            </Typography>
                                            <Typography variant="body2" sx={{ marginY: 1 }}>
                                                - аудио — MP3, M4A, AAC, WAV, FLAC, OGG
                                            </Typography>
                                            <Typography variant="body2" sx={{ marginBottom: 2 }}>
                                                - размер файла для видео – 1.5 ГБ, для аудио – 100
                                                МБ
                                            </Typography>
                                            {!files ? (
                                                <FileUploader
                                                    multiple={false}
                                                    types={fileTypes}
                                                    classes="upload-record"
                                                    style={{
                                                        '&:focus-within': {
                                                            outline: 'none',
                                                        },
                                                    }}
                                                    handleChange={handleChange}
                                                    name="files"
                                                    id="files"
                                                    required>
                                                    <Button
                                                        endIcon={<UploadIcon />}
                                                        fullWidth
                                                        variant="contained"
                                                        color="secondary">
                                                        Выбрать запись
                                                    </Button>
                                                </FileUploader>
                                            ) : (
                                                <Grid alignItems="center">
                                                    <Grid
                                                        sx={{
                                                            mt: 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                        }}>
                                                        <Box
                                                            sx={{
                                                                border: '1px solid #E7E8EE',
                                                                padding: '8px',
                                                                borderRadius: '12px',
                                                                width: '36px',
                                                                height: '36px',
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                marginRight: '8px',
                                                            }}>
                                                            <InsertDriveFileOutlinedIcon
                                                                sx={{
                                                                    color: '#004B9F',
                                                                    fontSize: '20px',
                                                                }}
                                                            />
                                                        </Box>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                            }}>
                                                            {files.name}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                pl: '4px',
                                                                ml: 2,
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                            variant="body2"
                                                            color="#7F7F7F"
                                                            component="span">
                                                            {Math.round(files.size / 1000000)}
                                                            &nbsp;МБ
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            aria-label="delete"
                                                            color="primary"
                                                            onClick={handleDeleteFile}
                                                            sx={{ ml: 1 }}>
                                                            <CloseIcon />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            )}
                                        </>
                                    )}
                                    <TextField
                                        sx={{ marginTop: 4 }}
                                        margin="dense"
                                        id="title"
                                        name="title"
                                        label="Название"
                                        fullWidth
                                        value={formik.values.title}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.title && Boolean(formik.errors.title)}
                                        helperText={formik.touched.title && formik.errors.title}
                                        required
                                    />

                                    <TextField
                                        multiline
                                        rows={6}
                                        sx={{
                                            marginTop: 4,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '24px',
                                            },
                                        }}
                                        id="extra_context"
                                        name="extra_context"
                                        label="Контекст записи"
                                        fullWidth
                                        value={formik.values.extra_context}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.extra_context &&
                                            Boolean(formik.errors.extra_context)
                                        }
                                        helperText={
                                            formik.touched.extra_context &&
                                            formik.errors.extra_context
                                        }
                                    />
                                </Grid>
                                <Grid>
                                    <Typography color="text" variant="h5" sx={{ mt: 2 }}>
                                        Вид расшифровки *
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ marginY: 1, color: '#9195A1' }}>
                                        Выберите вид, на базе которого мы расшифруем запись
                                    </Typography>

                                    <FormControl
                                        required
                                        sx={{
                                            overflowX: 'hidden',
                                            overflowY: 'auto',
                                            maxHeight: 'calc(100dvh - 390px)',
                                        }}>
                                        <RadioGroup
                                            id="idMemoPrompt"
                                            name="idMemoPrompt"
                                            value={formik.values.idMemoPrompt}
                                            onChange={formik.handleChange}>
                                            {map(memoPrompt, (item, index) => {
                                                return (
                                                    <>
                                                        <FormControlLabel
                                                            labelPlacement="start"
                                                            sx={{
                                                                marginLeft: 0,
                                                                mb: 2,
                                                                p: 2,
                                                                borderRadius: '12px',
                                                                '&:has( > .Mui-checked)': {
                                                                    background: '#F9FAFB',
                                                                },
                                                                width: '100%',
                                                                border: '1px solid #F4F4F6',
                                                                justifyContent: 'space-between',
                                                            }}
                                                            value={item.id}
                                                            control={<Radio required />}
                                                            label={
                                                                <>
                                                                    <Typography
                                                                        color="text"
                                                                        variant="h6">
                                                                        {item.title}
                                                                    </Typography>
                                                                    <Typography
                                                                        color="text"
                                                                        variant="caption">
                                                                        {item.description}
                                                                    </Typography>
                                                                </>
                                                            }
                                                        />
                                                    </>
                                                )
                                            })}
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                            </InnerWrapper>
                        </>
                    )}

                    <ActionsWrapper>
                        <Button
                            disabled={
                                !(formik.isValid && formik.dirty) ||
                                !memoPrompt ||
                                (memoPrompt && memoPrompt.length === 0) ||
                                isLoadingCreateRecord ||
                                isLoadingUpdateRecord
                            }
                            loading={isLoadingUploadFile}
                            loadingPosition="start"
                            type="submit"
                            variant="contained">
                            {isLoadingUploadFile ? 'Загрузка...' : 'Расшифровать'}
                        </Button>
                    </ActionsWrapper>
                </form>
            </Grid>
            <Notistack
                isError={true}
                open={errorCreateRecord || errorUploadFile || fileChangeError}
                message={errorCreateRecord || errorUploadFile || fileChangeError}
                onClose={handleCloseSnackbar}
            />
            <Notistack
                open={showSucces && isFullAccess}
                message="Запись загружается. После полной загрузки она появится в списке."
                onClose={handleCloseSuccess}
            />
        </BlockWrapper>
    )
}
